import { defineSlotRecipe } from '@chakra-ui/react';

import { sharedMarkBase } from './Checkbox';
import { focusRing } from '../focusRing';

/*
 * Design system source: .claude/skills/monetizenow-design/preview/toggles.html
 *
 * 16×16 circle, 1.5px border tGray.lightPurple (#C8C5D4).
 * Checked: indigo border + 8×8 indigo dot centered (the circle itself stays
 * white — unlike the checkbox, a checked radio is never filled).
 *
 * v3 port notes:
 * - This file replaces v2's `Radio.ts`. v3 has no standalone `Radio`
 *   component or recipe: a radio is a `radioGroup` item, and the recipe key
 *   is `radioGroup`. Slots map from v2's `radioAnatomy` as
 *   `container` → `item`, `control` → `itemControl`, `label` → `itemText`.
 * - The dot is `RadioGroup.ItemIndicator`, which renders the visible circle
 *   *in place of* `RadioGroup.ItemControl` (it wraps one with `asChild`) and
 *   contains a `<span class="dot">` only while checked. Call sites therefore
 *   render `ItemIndicator`, not `ItemControl`, and the dot is sized here via
 *   `& .dot` rather than v2's `_before` pseudo-element. `ItemIndicator`
 *   renders its mark *unstyled*, so every dot style has to come from the
 *   `itemControl` slot below — there is no built-in fallback.
 * - v2 spaced the label with `marginInlineStart: 2` inherited from the
 *   checkbox theme; `item` is a flex container with a `gap` in v3, so each
 *   size pins `gap: 2` (8px) instead — see the same note in `Checkbox.ts`.
 * - Chakra's built-in `radioGroup` recipe defaults to `variant: 'solid'` and
 *   deep-merges *under* this one, so `solid` is redefined below. Its checked
 *   state fills the circle with `colorPalette.solid`, which is precisely the
 *   treatment this design system does not use.
 */

/**
 * Exported for the rare call site that renders a radio-shaped mark outside
 * any `RadioGroup.Root` (see `ExternalDealRoomQuote.tsx`, which drives
 * selection with a manual click handler across sibling `Tab`s rather than
 * Ark's group context) — those use `Radiomark` directly, which reads its own
 * `radiomark` recipe key, not this file's `radioGroup` slot recipe. Reusing
 * this object keeps both call paths visually identical without duplicating
 * the design's border/checked/dot values a second time.
 */
export const itemControlBase = {
  ...sharedMarkBase,
  // The built-in radiomark sets its own `cursor: radio` (→ `default`) on the
  // control, so the item's `pointer` below never reaches the circle itself.
  cursor: 'pointer',
  borderRadius: 'full',
  // `.dot` paints itself with `currentColor`, so the dot color is this
  // slot's `color` in each state.
  color: 'tIndigo.base',
  // Override the checkbox's `_hover`, which fills the control. A radio stays
  // white inside on hover — only the border deepens.
  _hover: {
    borderColor: 'tIndigo.base',
    bg: 'bg.surface',
  },
  _checked: {
    background: 'transparent',
    borderColor: 'tIndigo.base',
    color: 'tIndigo.base',
    _hover: {
      background: 'transparent',
      borderColor: 'tIndigo.600',
      color: 'tIndigo.600',
    },
    // v2's checked+disabled radio kept the built-in checkbox control's gray
    // fill (gray.200) under the dot; `border.subtle` is the same gray.
    _disabled: {
      bg: 'border.subtle',
      borderColor: 'fg.disabled',
      color: 'fg.disabled',
    },
  },
  _focusVisible: focusRing(),
  // 50% of the control — 8px at the default 16px size. The built-in
  // radiomark ships 0.4.
  '& .dot': {
    scale: '0.5',
  },
};

const itemTextBase = {
  mt: '-1px',
  lineHeight: '18px',
  color: 'fg.primary',
  _disabled: {
    opacity: 1,
    color: 'fg.muted',
  },
};

export const radioGroupTheme = defineSlotRecipe({
  slots: [
    'root',
    'label',
    'item',
    'itemText',
    'itemControl',
    'indicator',
    'itemAddon',
    'itemIndicator',
  ],
  base: {
    // v3's built-in `itemControl` cursor token (`radio`) resolves to
    // `default`, not `pointer` — pin the whole item (control + label) to
    // `pointer` so the click target reads as interactive.
    item: {
      gap: 2,
      fontWeight: 'normal',
      cursor: 'pointer',
    },
    itemControl: itemControlBase,
    itemText: itemTextBase,
  },
  variants: {
    variant: {
      // Redefines the built-in `solid` variant — see the header note. These
      // are the exact keys `radiomarkRecipe.variants.variant.solid` sets.
      solid: {
        itemControl: {
          borderWidth: '1.5px',
          borderColor: 'tGray.lightPurple',
          _checked: {
            bg: 'transparent',
            color: 'tIndigo.base',
            borderColor: 'tIndigo.base',
          },
        },
      },
    },
    size: {
      sm: {
        item: { gap: 2 },
        itemControl: { boxSize: 3.5 },
        itemText: { fontSize: 'sm' },
      },
      md: {
        item: { gap: 2 },
        itemControl: { boxSize: 4 }, // 16px — design system default
        itemText: { fontSize: 'sm' },
      },
      lg: {
        item: { gap: 2 },
        itemControl: { boxSize: 5 },
        itemText: { fontSize: 'lg' },
      },
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

export default radioGroupTheme;
